import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Layout, Menu, Flex, Typography, Result, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router-dom';
import { useAuthStateContext } from 'providers/Auth';
import { fetchAttributeGroups } from 'services/api/attributes';
import { AttributeGroup } from 'types/api/attributes';
import { fetchDoctorById } from 'services/api/doctors';
import { DoctorProfile as DoctorProfileType } from 'types/api/doctors';

import { PageWrapper } from 'components/ui';
import {
  ProfileCompleteness,
  ChangeDoctorStatus,
  DoctorInfo,
  PlansModal,
  Send
} from './components';
import { SIDEBAR_OPTIONS } from './_constants';
import { StyledLayout } from './styled';
import { DynamicPage } from './pages';
import { LanguageSwitcher } from 'components/shared';
import { pickLocale } from 'utils/pickLocale';
import { DoctorProfileContext } from './DoctorProfileContext';

const { Content, Sider } = Layout;
const { Title } = Typography;

const Profile: FC = () => {
  const { id, page } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const [specialtyError, setSpecialtyError] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();

  const lang = useMemo(() => searchParams.get('lang'), [searchParams]);

  const { languages } = useAuthStateContext();

  const allowToEdit = languages.find(item => item.locale === lang)?.allowEdit;

  const [profile, setProfile] = useState<DoctorProfileType | null>(null);
  const [isFetchingProfile, setIsFetchingProfile] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!id || !lang) return;
    let cancelled = false;

    const load = async () => {
      try {
        setIsFetchingProfile(true);
        const res = await fetchDoctorById(id, lang);
        if (!cancelled) setProfile(res);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        if (!cancelled && (err?.message ?? '').includes('Specialty.name.')) {
          setSpecialtyError(true);
        }
      } finally {
        if (!cancelled) setIsFetchingProfile(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [id, lang, refreshKey]);

  const refreshProfile = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  const [attributeGroups, setAttributeGroups] = useState<AttributeGroup[]>([]);

  const [isFetchingAttributeGroups, setIsFetchingAttributeGroups] =
    useState(true);

  useEffect(() => {
    let cancelled = false;

    // Attribute-group NAMES + attribute field LABELS are the schema —
    // they should follow the admin's UI language, not the URL `?lang`
    // (which scopes the DATA locale being edited). Otherwise switching
    // the URL lang leaves the sidebar tab names + form labels
    // localized while the rest of the admin stays in the UI language
    // — the mixed EN/RU display we saw on the Membership tab.
    const load = async () => {
      try {
        setIsFetchingAttributeGroups(true);
        const res = await fetchAttributeGroups(i18n.language || 'en');
        if (!cancelled) setAttributeGroups(res.data ?? []);
      } catch {
        // swallow — sidebar renders the static items only on failure
      } finally {
        if (!cancelled) setIsFetchingAttributeGroups(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [i18n.language]);

  useEffect(() => {
    if (!lang) {
      searchParams.set('lang', 'en');
      setSearchParams(searchParams);
    }
  }, [lang, searchParams, setSearchParams]);

  const resetToEnglish = () => {
    searchParams.set('lang', 'en');
    setSearchParams(searchParams);
    setSpecialtyError(false);
  };

  const sidebarMenu = useMemo(
    () => [
      ...SIDEBAR_OPTIONS.map(item => ({
        key: item.slug,
        label: (
          <Link to={`/access/doctors/${id}/${item.slug}?lang=${lang}`}>
            {item.name}
          </Link>
        )
      })),
      ...(attributeGroups?.map(item => ({
        key: `${item?.slug}`,
        label: (
          <Link to={`/access/doctors/${id}/${item?.slug}?lang=${lang}`}>
            {item?.name}
          </Link>
        )
      })) || [])
    ],
    [attributeGroups, id, lang]
  );

  const activePage = useMemo(() => {
    const staticPage = SIDEBAR_OPTIONS.find(item => item.slug === page);

    if (staticPage) {
      return staticPage;
    }

    return attributeGroups?.find(item => item?.slug === page);
  }, [attributeGroups, page]);

  const activeAttributeGroup = useMemo(() => {
    if (!activePage || 'component' in activePage) return null;

    return attributeGroups.find(group => group.id === activePage.id) ?? null;
  }, [activePage, attributeGroups]);

  const attributeGroupId = useMemo(() => {
    if (activePage && 'component' in activePage) {
      return '';
    }

    return profile?.groups?.find(item => item?.group_id === activePage?.id)?.id;
  }, [activePage, profile?.groups]);

  const PageComponent =
    activePage && 'component' in activePage
      ? activePage.component
      : DynamicPage;

  if (specialtyError) {
    return (
      <Flex style={{ height: 'calc(100vh - 48px)' }} justify="center" vertical>
        <Result
          status="error"
          title="Complete Specialties First"
          subTitle="Please fill in all specialties in the selected language before continuing"
          extra={
            <Flex gap={16} justify="center">
              <Button type="default" onClick={resetToEnglish}>
                Cancel
              </Button>
              <Button type="primary" onClick={() => navigate('/specialties')}>
                Go to Specialties
              </Button>
            </Flex>
          }
        />
      </Flex>
    );
  }

  const breadcrumbName = profile
    ? `${pickLocale(profile.first_name, lang)} ${pickLocale(
        profile.last_name,
        lang
      )}`.trim()
    : '';

  return (
    <DoctorProfileContext.Provider
      value={{ profile, loading: isFetchingProfile, refresh: refreshProfile }}
    >
      <PageWrapper
        back
        loading={isFetchingProfile || isFetchingAttributeGroups || !lang}
        onClickBack={() => navigate('/access/doctors')}
        breadcrumbs={{
          items: [
            {
              title: 'Users'
            },
            {
              title: <Link to="/access/doctors">Doctors</Link>
            },
            {
              title: breadcrumbName
            }
          ]
        }}
      >
        <StyledLayout>
          <Sider width={340}>
            <DoctorInfo
              id={`${id}`}
              lang={`${lang}`}
              allowToEdit={allowToEdit}
            />
            <Flex style={{ marginBottom: 40 }} gap={12}>
              <Send id={`${id}`} lang={`${lang}`} allowToEdit={allowToEdit} />
              <LanguageSwitcher lang={`${lang}`} />
            </Flex>
            <Menu selectedKeys={[`${page}`]} items={sidebarMenu} />
            <ProfileCompleteness
              id={`${id}`}
              lang={`${lang}`}
              attributeGroups={attributeGroups}
            />
          </Sider>
          <Content>
            <Flex
              style={{ marginBottom: 32 }}
              justify="space-between"
              align="center"
            >
              <Title level={3} style={{ margin: 0 }}>
                {activePage?.name}
              </Title>
              <ChangeDoctorStatus id={`${id}`} lang={`${lang}`} />
            </Flex>
            {PageComponent && (
              <PageComponent
                id={`${id}`}
                pageId={`${activePage?.id}`}
                lang={`${lang}`}
                doctorGroupId={
                  attributeGroupId != null
                    ? String(attributeGroupId)
                    : undefined
                }
                allowToEdit={allowToEdit}
                attributeGroup={activeAttributeGroup}
              />
            )}
          </Content>
        </StyledLayout>
        <PlansModal />
      </PageWrapper>
    </DoctorProfileContext.Provider>
  );
};

export default Profile;
